/**
 * Standalone-page layout tokens and breakpoint hooks used by the two-column
 * question layout.
 *
 * Pins the magic numbers in one place: 600px left rail, 360px right rail, 2.5vw
 * column gaps, and a 1050px desktop breakpoint below which the right rail
 * collapses to the bottom of the page. The exported `LAYOUT_SINGLE_COLUMN` /
 * `LAYOUT_TWO_COLUMN` arrays feed CSS Grid `gridTemplateColumns` directly.
 * `useShowRightRail` combines the desktop breakpoint with the sidebar state —
 * the rail never appears inside the sidebar, regardless of width.
 */

import { useWindowDimensions } from 'react-native';
import { useIsInSidebar } from 'util/datastore';

/**
 * Large breakpoint
 */
export const LAYOUT_BREAKPOINT_LARGE = 1050;

/**
 * Width of the main content column in standalone layout (left rail).
 */
export const LAYOUT_LEFT_RAIL_WIDTH = 600;

/**
 * Width of the right rail column in standalone layout.
 */
const LAYOUT_RIGHT_RAIL_WIDTH = 360;

/**
 * Gap between columns in standalone layout grid (in viewport width units).
 */
const LAYOUT_COLUMN_GAP = '2.5vw';

/**
 * Layout single column grid.
 */
export const LAYOUT_SINGLE_COLUMN = [
    '1fr', // left margin
    `[content-start] min(${LAYOUT_LEFT_RAIL_WIDTH}px, 100%) [content-end]`, // centered content
    '1fr', // right margin
];

/**
 * Layout two column grid.
 */
export const LAYOUT_TWO_COLUMN = [
    '1fr', // left margin
    `[content-start] ${LAYOUT_LEFT_RAIL_WIDTH}px [content-end]`, // left rail (600px)
    LAYOUT_COLUMN_GAP, // gap (2.5vw)
    '[divider-start] 1px [divider-end]', // vertical divider
    LAYOUT_COLUMN_GAP, // gap (2.5vw)
    `[rail-start] ${LAYOUT_RIGHT_RAIL_WIDTH}px [rail-end]`, // right rail (360px)
    '1fr', // right margin
];

/**
 * Hook that returns the current window width and updates on resize.
 */
function useWindowWidth() {
    return useWindowDimensions().width;
}

/**
 * Returns whether the window width meets the desktop breakpoint.
 */
export function useIsDesktopBreakpoint() {
    const windowWidth = useWindowWidth();
    return windowWidth >= LAYOUT_BREAKPOINT_LARGE;
}

export function useShowRightRail() {
    const isInSidebar = useIsInSidebar();
    const hasDesktopWidth = useIsDesktopBreakpoint();
    return !isInSidebar && hasDesktopWidth;
}
